import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import ImagePickerDialog from './ImagePickerDialog';

const NewsManager = () => {
  const [news, setNews] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: { sv: '', en: '' },
    content: { sv: '', en: '' },
    excerpt: { sv: '', en: '' },
    image_url: '',
  });

  const loadNews = async () => {
    const data = await api.getNews(50);
    setNews(data);
  };

  useEffect(() => {
    loadNews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await api.updateNews(editingNews.id, formData);
        toast.success('Nyhet uppdaterad!');
      } else {
        await api.createNews(formData);
        toast.success('Nyhet skapad!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadNews();
    } catch (error) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker?')) {
      await api.deleteNews(id);
      toast.success('Nyhet raderad!');
      loadNews();
    }
  };

  const resetForm = () => {
    setFormData({
      title: { sv: '', en: '' },
      content: { sv: '', en: '' },
      excerpt: { sv: '', en: '' },
      image_url: '',
    });
    setEditingNews(null);
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({
      title: item.title,
      content: item.content,
      excerpt: item.excerpt,
      image_url: item.image_url || '',
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="news-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Nyheter</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-news-button">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till Nyhet
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingNews ? 'Redigera Nyhet' : 'Lägg till Nyhet'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="news-form">
              <div>
                <label className="block text-sm font-medium mb-2">Titel (Svenska)</label>
                <Input
                  required
                  value={formData.title.sv}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, sv: e.target.value } })}
                  data-testid="news-title-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Innehåll (Svenska)</label>
                <Textarea
                  required
                  rows={6}
                  value={formData.content.sv}
                  onChange={(e) => setFormData({ ...formData, content: { ...formData.content, sv: e.target.value } })}
                  data-testid="news-content-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sammanfattning (Svenska)</label>
                <Textarea
                  required
                  rows={3}
                  value={formData.excerpt.sv}
                  onChange={(e) => setFormData({ ...formData, excerpt: { ...formData.excerpt, sv: e.target.value } })}
                  data-testid="news-excerpt-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bild URL</label>
                <div className="flex items-center gap-2 mb-2">
                  <ImagePickerDialog 
                    currentImage={formData.image_url}
                    onSelect={(url) => setFormData({ ...formData, image_url: url })}
                  />
                  <span className="text-xs text-gray-500">eller ange URL:</span>
                </div>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="news-image-url"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="news-submit">
                {editingNews ? 'Uppdatera' : 'Skapa'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {news.map((item) => (
          <Card key={item.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{item.title.sv}</h3>
                  <p className="text-gray-600 line-clamp-2">{item.excerpt.sv}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {new Date(item.published_at).toLocaleDateString('sv-SE')} • {item.source}
                  </p>
                </div>
                <div className="flex space-x-2">
                  {item.source === 'local' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-news-${item.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(item.id)} data-testid={`delete-news-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default NewsManager;
